import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { isNativeApp } from '../lib/platform';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Send credentials globally so session_token cookie is included
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser({
        username: response.data.username,
        email: response.data.email,
        name: response.data.name,
        picture: response.data.picture,
        auth_type: response.data.auth_type,
        has_password: response.data.has_password
      });
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const exchangeGoogleSession = useCallback(async (sessionId) => {
    const response = await axios.post(
      `${API_URL}/api/auth/google/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser({
      username: response.data.name || response.data.email,
      email: response.data.email,
      name: response.data.name,
      picture: response.data.picture,
      auth_type: 'google',
      has_password: Boolean(response.data.has_password)
    });
    return response.data;
  }, []);

  // Native deep-link listener: when the OAuth bridge deep-links back via
  // com.emergent.farmworker://oauth?session_id=..., extract and exchange the session.
  useEffect(() => {
    if (!isNativeApp()) return;

    let cleanup;
    (async () => {
      try {
        const [{ App: CapApp }, { Browser }] = await Promise.all([
          import('@capacitor/app'),
          import('@capacitor/browser'),
        ]);

        const listener = await CapApp.addListener('appUrlOpen', async (event) => {
          const url = event?.url || '';
          if (!url.includes('session_id=')) return;

          try {
            const parsed = new URL(url);
            // session_id may be in query OR fragment depending on the bridge
            const sessionId =
              parsed.searchParams.get('session_id') ||
              new URLSearchParams(parsed.hash.replace(/^#/, '')).get('session_id');

            if (!sessionId) return;

            await Browser.close().catch(() => {});
            await exchangeGoogleSession(sessionId);
            // Redirect to dashboard inside the native WebView
            window.location.href = '/dashboard';
          } catch (e) {
            if (process.env.NODE_ENV !== 'production') {
              console.warn('Native OAuth deep-link exchange failed:', e?.message);
            }
          }
        });

        cleanup = () => listener.remove();
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Capacitor plugins unavailable (running as web build):', e?.message);
        }
      }
    })();

    return () => {
      if (cleanup) cleanup();
    };
  }, [exchangeGoogleSession]);

  const loginWithGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isNativeApp()) {
      // Native: open OAuth in system browser and land on the bridge page,
      // which will deep-link back into the app.
      const { Browser } = await import('@capacitor/browser');
      const bridgeUrl = `${API_URL}/oauth-bridge`;
      await Browser.open({
        url: `https://auth.emergentagent.com/?redirect=${encodeURIComponent(bridgeUrl)}`,
        presentationStyle: 'fullscreen',
      });
      return;
    }

    // Web (browser / PWA)
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = useCallback(async (email, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser({
      username: response.data.name || response.data.email,
      email: response.data.email,
      name: response.data.name,
      picture: response.data.picture,
      auth_type: 'password'
    });
    return response.data;
  }, []);

  const registerWithPassword = useCallback(async (email, password, name) => {
    const response = await axios.post(
      `${API_URL}/api/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    setUser({
      username: response.data.name || response.data.email,
      email: response.data.email,
      name: response.data.name,
      picture: response.data.picture,
      auth_type: 'password'
    });
    return response.data;
  }, []);

  const setPassword = useCallback(async (password) => {
    await axios.post(`${API_URL}/api/auth/set-password`, { password }, { withCredentials: true });
    setUser((prev) => (prev ? { ...prev, has_password: true } : prev));
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Server logout failed, clearing local state anyway:', e?.message);
      }
    }
    setUser(null);
  };

  const contextValue = useMemo(() => ({
    user,
    loginWithGoogle,
    loginWithPassword,
    registerWithPassword,
    setPassword,
    exchangeGoogleSession,
    logout,
    loading
  }), [user, exchangeGoogleSession, loginWithPassword, registerWithPassword, setPassword, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
